<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="transfer">
  <app-page-detail-hero [auditStamp]="transfer" icon="pi pi-sync" eyebrow="Warehouse Transfer" [title]="transfer.transferNumber || 'Transfer'" [subtitle]="clientName(transfer.crmClientId)">
    <div tags class="flex flex-wrap gap-2">
      <p-tag [value]="transfer.status" [severity]="statusSeverity(transfer.status)"></p-tag>
      <span>{{ masterName('WAREHOUSE', transfer.sourceWarehouseId) }} -> {{ masterName('WAREHOUSE', transfer.destinationWarehouseId) }}</span>
      <span>Scheduled {{ displayDate(transfer.scheduledDate) }}</span>
    </div>
    <div actions class="flex flex-wrap gap-2">
      @if (canReserve()) { <p-button label="Reserve" icon="pi pi-lock" severity="warn" [outlined]="true" [loading]="isSubmitting" [disabled]="isSubmitting" (click)="reserveTransfer()"></p-button> }
      <app-warehousing-supply-reserve documentType="TRANSFER" [documentId]="transfer.id!"
        [items]="items" [zones]="masterData.ZONE" [conditions]="masterData.ITEM_CONDITION"
        [canEdit]="canChooseSupply()" [autoOpen]="supplyAutoOpen()"
        (changed)="loadTransfer()"></app-warehousing-supply-reserve>
      @if (canScanOutbound()) { <p-button label="Scan Units" icon="pi pi-barcode" severity="secondary" [outlined]="true" routerLink="pick-scan" queryParamsHandling="preserve"></p-button> }
      @if (canReceive()) { <p-button label="Receive" icon="pi pi-sign-in" severity="success" routerLink="receive-scan" queryParamsHandling="preserve"></p-button> }
      @if (canCancel()) { <p-button label="Cancel" icon="pi pi-times" severity="danger" [text]="true" [loading]="isSubmitting" [disabled]="isSubmitting" (click)="cancelTransfer()"></p-button> }
      @if (transfer.crossDockOrchestrationId) {
        <p-button label="Orchestration" icon="fa-solid fa-network-wired" severity="secondary" [outlined]="true" [routerLink]="['/transport-planning/orchestrations', transfer.crossDockOrchestrationId]"></p-button>
      }
      <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" [routerLink]="backLink()" queryParamsHandling="preserve"></p-button>
    </div>
  </app-page-detail-hero>

  <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>
  <app-warehousing-desktop-outbound [transfer]="transfer"
    [disabledReason]="usesVirtualWarehouse() ? 'Physical stock-ledger warehouse required.' : ''"
    (changed)="loadTransfer()"></app-warehousing-desktop-outbound>
  <app-warehousing-work-history documentType="TRANSFER" [documentId]="transfer.id"></app-warehousing-work-history>

  <app-page-data-panel title="Transfer Summary">
    <div class="grid">
      <div class="col-12 md:col-3"><strong>Client</strong><br>{{ clientName(transfer.crmClientId) }}</div>
      <div class="col-12 md:col-3"><strong>Account</strong><br>{{ accountName(transfer.crmAccountId) }}</div>
      <div class="col-12 md:col-3"><strong>Reference</strong><br>{{ transfer.referenceNumber || '-' }}</div>
      <div class="col-12 md:col-3"><strong>Scheduled</strong><br>{{ displayDate(transfer.scheduledDate) }}</div>
      <div class="col-12 md:col-3"><strong>Source</strong><br>{{ masterName('REGION', transfer.sourceRegionId) }} / {{ masterName('WAREHOUSE', transfer.sourceWarehouseId) }}</div>
      <div class="col-12 md:col-3"><strong>Destination</strong><br>{{ masterName('REGION', transfer.destinationRegionId) }} / {{ masterName('WAREHOUSE', transfer.destinationWarehouseId) }}</div>
      <div class="col-12 md:col-3"><strong>Dispatch Date</strong><br>{{ displayDate(transfer.dispatchDate) }}</div>
      <div class="col-12 md:col-3"><strong>Receipt Date</strong><br>{{ displayDate(transfer.receiptDate) }}</div>
      @if (transfer.crossDockOrchestrationId) {
        <div class="col-12 md:col-3">
          <strong>Cross-Dock Orchestration</strong><br>
          <a [routerLink]="['/transport-planning/orchestrations', transfer.crossDockOrchestrationId]">
            {{ transfer.crossDockOrchestrationNumber || ('#' + transfer.crossDockOrchestrationId) }}
          </a>
        </div>
        <div class="col-12 md:col-3"><strong>Gated By Transfer</strong><br>{{ transfer.gatedByTransferId || '-' }}</div>
        <div class="col-12 md:col-3">
          <strong>Receipt Gate</strong><br>
          <p-tag [value]="gateStatusLabel()" [severity]="gateStatusSeverity()"></p-tag>
        </div>
      }
      <div class="col-12"><strong>Notes</strong><br>{{ transfer.notes || '-' }}</div>
    </div>
  </app-page-data-panel>

  <app-page-form-section title="Lines" subtitle="Source and destination handling instructions per item.">
    <p-table [value]="transfer.lines || []" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [tableStyle]="{ 'min-width': '58rem' }">
      <ng-template pTemplate="header"><tr><th>Item</th><th>Source</th><th>Destination</th><th>Quantities</th><th>Work Progress</th></tr></ng-template>
      <ng-template pTemplate="body" let-line>
        <tr>
          <td>{{ itemName(line.itemId) }}</td>
          <td>{{ masterName('ZONE', line.sourceZoneId) }} / {{ masterName('ITEM_CONDITION', line.sourceConditionId) }}</td>
          <td>{{ masterName('ZONE', line.destinationZoneId) }} / {{ masterName('ITEM_CONDITION', line.destinationConditionId) }}</td>
          <td>Req {{ line.quantityRequested || 0 }} | Res {{ line.quantityReserved || 0 }} | Out {{ line.quantityDispatched || 0 }} | In {{ line.quantityReceived || 0 }}</td>
          <td>{{ scanSummary(line) }}</td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="5">No transfer lines.</td></tr></ng-template>
    </p-table>
  </app-page-form-section>

  <app-page-form-section title="Reserved Stock Units" subtitle="The same unit IDs move from source dispatch to destination receipt.">
    <p-table [value]="reservationRows()" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [tableStyle]="{ 'min-width': '64rem' }">
      <ng-template pTemplate="header"><tr><th>Unit</th><th>Item</th><th>Qty</th><th>Outbound</th><th>Receipt</th><th>Picked</th><th>Staged</th><th>Loaded</th><th>Received</th></tr></ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td><span class="warehousing-code-pill">{{ row.reservation.stockUnitCode || ('Unit #' + row.reservation.stockUnitId) }}</span></td>
          <td>{{ itemName(row.itemId) }}</td>
          <td>{{ row.reservation.quantity || 0 }}</td>
          <td><p-tag [value]="row.reservation.scanStatus || 'PENDING'" [severity]="statusSeverity(row.reservation.scanStatus)"></p-tag></td>
          <td><p-tag [value]="row.reservation.receiptStatus || 'PENDING'" [severity]="statusSeverity(row.reservation.receiptStatus)"></p-tag></td>
          <td>{{ displayDateTime(row.reservation.pickedAt) }}</td>
          <td>{{ displayDateTime(row.reservation.stagedAt) }}</td>
          <td>{{ displayDateTime(row.reservation.loadedAt) }}</td>
          <td>{{ displayDateTime(row.reservation.receivedAt) }}</td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="9">No reserved stock units yet.</td></tr></ng-template>
    </p-table>
  </app-page-form-section>
  <app-field-resources-panel
    *ngIf="transfer?.id"
    targetType="WAREHOUSE_TRANSFER"
    [targetId]="transfer.id"
    [targetReference]="transfer.transferNumber"
    [customerCrmClientId]="transfer.crmClientId">
  </app-field-resources-panel>
</app-page-shell>
